import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { ConfigItemsLayout } from "./ConfigItemsLayout";
import {
  DeleteConfirmationModal,
  FileUploadIcon,
  ProfileUploadComponent,
  UploadIcon2,
} from "components";
import { useState } from "react";
import { uploadEmailTemplateAttachment } from "controller";
import toast from "react-hot-toast";

export const LogoConfigItem = ({ isLoading }: { isLoading: boolean }) => {
  const theme = useTheme();
  const { watch, setValue } = useFormContext();
  const imgUrl = watch("signature_logo");
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to convert file to base64"));
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const getImageDimensions = (
    file: File,
  ): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
        URL.revokeObjectURL(img.src);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
        URL.revokeObjectURL(img.src);
      };
    });
  };

  const handleFileUpload = async (file: File) => {
    try {
      // Get image dimensions first
      const dimensions = await getImageDimensions(file);
      const aspectRatio = dimensions.height / dimensions.width;

      const formData = new FormData();
      formData?.append("file", file);
      setIsUploadingFile(true);
      uploadEmailTemplateAttachment({
        formData,
      }).subscribe({
        next: ({ response, status, percentage, controller }) => {
          if (status === "COMPLETE") {
            setIsUploadingFile(false);
            if (response?.success && response?.statusCode === 200) {
              setValue("signature_logo", response?.data?.url, {
                shouldDirty: true,
              });
              setValue("width", 100, {
                shouldDirty: true,
              });
              setValue("height", 100 * aspectRatio, {
                shouldDirty: true,
              });
              setValue("is_size_synced", true);
            } else {
              setIsUploadingFile(false);
              toast.error("Failed to upload image. Please try again");
            }
          }
        },
      });
    } catch (error) {
      console.error("Error processing image:", error);
    }
  };

  return (
    <ConfigItemsLayout
      title="Logo"
      description="Upload a PNG, JPG or GIF image smaller than 1MB."
      icon={<FileUploadIcon sx={{ width: 20, height: 20 }} />}
    >
      <DeleteConfirmationModal
        open={deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(false)}
        title="Delete Logo"
        tagline="Are you sure you want to delete your logo?"
        onSecondaryButtonClick={() => {
          setDeleteConfirmationModalOpen(false);
        }}
        onPrimaryButtonClick={() => {
          setValue("signature_logo", null);
          setValue("width", 100);
          setValue("height", 100);
          setDeleteConfirmationModalOpen(false);
        }}
      />
      <ProfileUploadComponent
        imageSizeLimit={1048576}
        onAttachFile={(file) => {
          if (!isLoading) {
            handleFileUpload(file);
          }
        }}
        loading={isUploadingFile}
        url={imgUrl}
        sx={{
          borderRadius: "4px",
          width: "200px",
          height: "70px",
        }}
        onImgFileDelete={() => setDeleteConfirmationModalOpen(true)}
        disabled={isLoading}
        customPlaceholder={({ disabled, onClick }) => {
          return (
            <Stack
              direction={"row"}
              sx={{
                border: `1px dashed ${theme.palette.neutral["015"]}`,
                borderRadius: "8px",
                width: "200px",
                height: "70px",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                position: "relative",
                gap: "4px",
                "&:hover": {
                  ".hover-effect": {
                    opacity: 1,
                    transition: "opacity 0.2s ease",
                  },
                },
                "&:not(:hover)": {
                  ".hover-effect": {
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  },
                },
              }}
              // @ts-ignore
              onClick={() => !disabled && onClick()}
            >
              <UploadIcon2
                sx={{ width: "20px", height: "20px" }}
                htmlColor={
                  disabled
                    ? theme.palette.lightBg.low
                    : theme.palette.primary.main
                }
              />
              <Typography
                variant="link-small"
                sx={{
                  fontWeight: "400",
                  fontSize: "12px",
                }}
              >
                Upload your logo
              </Typography>
              <Box
                className="hover-effect"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "8px",
                  justifyContent: "center",
                  bgcolor: theme.palette.neutral["015"],
                  backdropFilter: "blur(4px)",
                }}
              >
                <UploadIcon2
                  sx={{ width: "28px", height: "28px" }}
                  htmlColor={
                    disabled
                      ? theme.palette.lightBg.low
                      : theme.palette.primary.main
                  }
                />
              </Box>
            </Stack>
          );
        }}
      />
    </ConfigItemsLayout>
  );
};
